import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, Link, Stack, Typography} from '@mui/material';
import TitleField from './documentHeader/TitleField';
import AuthorsField from './documentHeader/AuthorsField';
import {AutoAwesome} from '@mui/icons-material';
import {
    DEFAULT_DISCLOSURE_HEADER_DATA,
    DISCLOSURE_TYPE_OPTIONS,
    PURPOSE_OPTIONS
} from './documentHeader/disclosureFieldOptions';
import {subscribeToDocument} from '../firestore/documents';
import useGenerateInitialVersionStore from '../../../stores/useGenerateInitialVersionStore';

export default function DisclosureDocumentHeader({documentId, initialData = {}, onChange}) {
    const [title, setTitle] = useState('');
    const [headerData, setHeaderData] = useState({
        ...DEFAULT_DISCLOSURE_HEADER_DATA,
        ...initialData
    });
    const [computedClassification, setComputedClassification] = useState('');
    const onChangeRef = useRef(onChange);

    const [authors, setAuthors] = useState([]);
    const [parentProduct, setParentProduct] = useState(null);

    // Get the openModal action from the store
    const openModal = useGenerateInitialVersionStore(s => s.openModal);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!documentId) {
            return;
        }

        const unsubscribe = subscribeToDocument(
            documentId,
            (docData) => {
                if (!docData) return;

                if (docData.title !== undefined) {
                    setTitle(docData.title);
                }

                const meta = docData.meta || {};
                setAuthors(meta.authors || []);
                setParentProduct({
                    title: docData?.parent_product?.title, uid: docData?.parent_product?.uid || null
                });

                const disclosureFields = {
                    disclosureType: meta.disclosureType || DEFAULT_DISCLOSURE_HEADER_DATA.disclosureType,
                    selectedCountries: meta.selectedCountries || '',
                    purpose: meta.purpose || DEFAULT_DISCLOSURE_HEADER_DATA.purpose,
                    additionalInstructions: meta.additionalInstructions || '',
                    applyPolicy: meta.applyPolicy || DEFAULT_DISCLOSURE_HEADER_DATA.applyPolicy,
                    displayOnly: meta.displayOnly || false
                };

                setHeaderData({
                    ...DEFAULT_DISCLOSURE_HEADER_DATA,
                    ...disclosureFields
                });

                if (onChangeRef.current) {
                    onChangeRef.current({
                        title: docData.title,
                        authors: meta.authors || [],
                        parentProduct: meta.parentProduct || meta.productId || null,
                        ...disclosureFields
                    });
                }
            },
            (error) => {
                console.error('[DisclosureDocumentHeader] Error subscribing to document:', error);
            }
        );

        return () => {
            unsubscribe();
        };
    }, [documentId]);

    useEffect(() => {
        const country = headerData.selectedCountries || '';
        if (!country) {
            setComputedClassification('Select country');
            return;
        }

        if (headerData.displayOnly) {
            setComputedClassification(`DISPLAY ONLY ${country}`);
        } else {
            setComputedClassification(`REL TO ${country}`);
        }
    }, [headerData.selectedCountries, headerData.displayOnly]);

    const showNDP1Fields = headerData.disclosureType?.value === 'NDP-1';

    const renderRow = (label, value) => (
        <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
            <Typography variant="body2" color="text.secondary" sx={{minWidth: '150px', pt: 0.5}}>
                {label}:
            </Typography>
            <Typography variant="body1" sx={{pt: 0.5}}>
                {value || '—'}
            </Typography>
        </Box>
    );

    const disclosureTypeLabel = DISCLOSURE_TYPE_OPTIONS.find(opt => opt.value === headerData.disclosureType?.value)?.label || headerData.disclosureType?.label || '';
    const purposeLabel = PURPOSE_OPTIONS.find(opt => opt.value === headerData.purpose?.value)?.label || headerData.purpose?.label || '';

    const handleTitleChange = useCallback((newTitle) => {
        setTitle(newTitle);
        if (onChangeRef.current) {
            onChangeRef.current({
                title: newTitle,
                authors,
                parentProduct,
                ...headerData
            });
        }
    }, [headerData, authors, parentProduct]);

    const handleGenerate = useCallback(() => {
        console.log('🎯 [DisclosureDocumentHeader] Generate button clicked');
        console.log('📦 [DisclosureDocumentHeader] openModal function:', openModal);
        openModal();
        console.log('✅ [DisclosureDocumentHeader] openModal() called');
    }, [openModal]);

    return (
        <Stack spacing={2} p={3} zIndex={10} bgcolor="white" sx={{borderBottom: '1px solid #CECFD2'}}>
            <TitleField
                documentId={documentId}
                initialValue={title}
                onChange={handleTitleChange}
            />

            <AuthorsField authors={authors}/>

            <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                <Typography variant="body2" color="text.secondary" sx={{minWidth: '150px', pt: 0.5}}>
                    Parent product:
                </Typography>
                {parentProduct ? (
                    <Link
                        href={`/products/${parentProduct.uid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{pt: 0.5}}
                    >
                        {parentProduct.title}
                    </Link>
                ) : (
                    <Typography variant="body2" color="text.disabled" sx={{pt: 0.5}}>
                        —
                    </Typography>
                )}
            </Box>

            {renderRow('Disclosure Type', disclosureTypeLabel || 'Select disclosure type')}

            {showNDP1Fields && (
                <>
                    {renderRow('Country', headerData.selectedCountries || 'Select country')}
                    {renderRow('Purpose', purposeLabel || 'Select purpose')}
                    {renderRow('DISPLAY ONLY', headerData.displayOnly ? 'Yes' : 'No')}
                    {renderRow('Classification marking', computedClassification)}
                </>
            )}
            <Stack spacing={2} severity="info" p={2}
                   bgcolor="rgba(91, 143, 168, 0.1)"
                   borderRadius={2}
            >
                <Typography vasriant={'body1'}>
                    Try to generate Draft content. This won't replace your current content. Opens in a modal for review
                    before applying.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerate}
                    startIcon={<AutoAwesome/>}
                    size={'large'}
                >
                    Generate Initial Version
                </Button>

            </Stack>
        </Stack>
    );
}
